import React, { useState, useEffect } from 'react';
import axios from '../../../services/api';
import { FaTimes, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import CreatePublicServiceModal from './CreatePublicServiceModal';
import DeleteConfirmModal from '../../../components/modals/ConfirmModal';
import { useTranslation } from 'react-i18next';

const PublicServiceModal = ({ isOpen, onClose, community }) => {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen && community) {
      fetchServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, community]);
    // eslint-disable-next-line react-hooks/exhaustive-deps

  const fetchServices = async () => {
    try {
      const response = await axios.get(`/public-services/${community._id}`);
      setServices(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const filteredServices = services.filter(service =>
    service.institutionName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.contactName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-end pr-[10%] pt-[5%] z-50">
      <div className="bg-white w-[60vw] h-[70vh] rounded-lg shadow-xl flex flex-col">
        <div className="bg-black text-white px-4 py-3 rounded-t-lg flex justify-between items-center">
          <h2 className="text-lg font-semibold">{t('Public Service')}</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <FaTimes />
          </button>
        </div>

        <div className="p-4 bg-gradient-to-br from-skin-base to-skin-secondary app-text">
          <div className="flex justify-between items-center mb-4">
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder={t('Search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded pl-10 pr-4 py-2"
              />
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            <button
              onClick={() => {
                setSelectedService(null);
                setIsCreateModalOpen(true);
              }}
              className="px-4 py-2 border border-gray-300 rounded bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              {t('Create New')}
            </button>
          </div>

          <div className="overflow-auto flex-1 h-[calc(70vh-150px)]">
            <table className="w-full">
              <thead className="sticky top-0 z-30 bg-skin-primary shadow-sm">
                <tr className="border-b border-skin-base/10">
                  <th className="px-4 py-2 text-left font-semibold text-skin-base">{t('No')}</th>
                  <th className="px-4 py-2 text-left font-semibold text-skin-base">{t('Institution Name')}</th>
                  <th className="px-4 py-2 text-left font-semibold text-skin-base">{t('Job Title')}</th>
                  <th className="px-4 py-2 text-left font-semibold text-skin-base">{t('Contact Name')}</th>
                  <th className="px-4 py-2 text-left font-semibold text-skin-base">{t('Is Public')}</th>
                  <th className="px-4 py-2 text-left font-semibold text-skin-base w-[100px] text-right">{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredServices.map((service, index) => (
                  <tr key={service._id}>
                    <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">{index + 1}</td>
                    <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">{service?.institutionName}</td>
                    <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">{service?.jobTitle}</td>
                    <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">{service.contactName}</td>
                    <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">
                      {service.isPublic ? 'Yes' : 'No'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap border-b border-gray-200">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => {
                            setSelectedService(service);
                            setIsCreateModalOpen(true);
                          }}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedService(service);
                            setIsDeleteModalOpen(true);
                          }}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <CreatePublicServiceModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setSelectedService(null);
          }}
          community={community}
          service={selectedService}
          onSuccess={fetchServices}
        />

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedService(null);
          }}
          onConfirm={async () => {
            try {
              await axios.delete(`/public-services/${selectedService._id}`);
              toast.success(t('Public service deleted successfully'));
              setIsDeleteModalOpen(false);
              setSelectedService(null);
              fetchServices();
            } catch (error) {
              toast.error(t('Public service delete error'));
            }
          }}
          title={t('Delete Public Service')}
          message={t('Are you sure you want to delete this public service? This action cannot be undone.')}
        />
      </div>
    </div>
  );
};

export default PublicServiceModal;
